import {
  GetAiRunsRequest,
  PollAiRunsRequest
} from '@eggplant/api-requests'
import {
  InvisiButton,
  Paragraph,
  PollingIndicator,
  ResultsCount,
  ResultsFooter,
  Timezone,
  Tooltip
} from '@eggplant/pattern-library'
import type { CSSProperties } from 'react'
import type {
  GetRunsResponse,
  GetTestResultsParams
} from '@eggplant/types'
import type { HandleError } from '../types'

interface Props {
  isPolling: boolean
  intervalMs?: number
  onError?: HandleError
  onSuccess: (response: GetRunsResponse) => void
  onPollingToggle: () => void
  params: GetTestResultsParams
}

const style: CSSProperties = {
  display: 'grid',
  gap: '12px',
  gridAutoFlow: 'column',
  alignItems: 'start'
}

// Abstracts the switch between a single request and polling

const ResultsPollingToggle = ({
  isPolling,
  intervalMs = 5000,
  onError,
  onSuccess,
  onPollingToggle,
  params
}: Props) => {
  const buttonContent = (
    <PollingIndicator
      hasLabel
      isPolling={isPolling}
    />
  )

  const title = isPolling
    ? 'Updating results every 5 seconds. Click to stop.'
    : 'Refresh results and enable automatic updates.'

  const children = ({ total_count }: GetRunsResponse) =>
    <ResultsFooter>
      <Paragraph>
        <span style={style}>
          <ResultsCount value={total_count} />
          <Tooltip label={title}>
            <InvisiButton
              hasBackgroundColor={false}
              hasPadding={false}
              label={title}
              onClick={onPollingToggle}
            >
              {buttonContent}
            </InvisiButton>
          </Tooltip>
        </span>
      </Paragraph>
      <Timezone />
    </ResultsFooter>

  const props = {
    onError,
    onSuccess,
    params,
    children
  }

  return isPolling
    ? (
      <PollAiRunsRequest
        interval={intervalMs}
        isPolling={isPolling}
        key={JSON.stringify(params)} // refreshes the component to restart polling
        {...props}
      />
    )
    : (
      <GetAiRunsRequest
        {...props}
      />
    )
}

export default ResultsPollingToggle
